import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Field } from "../Shared";
import { DateField } from "../DateField";
import { PROG } from "../../constants/testIds";

export function BatchForm({ programId, initial, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!initial.batch_id;
  const [form, setForm] = useState({
    name: initial.name || "",
    code: initial.code || "",
    start_date: initial.start_date || "",
    end_date: initial.end_date || "",
    capacity: initial.capacity || 30,
    status: initial.status || "OPEN",
  });
  const [busy, setBusy] = useState(false);
  const [program, setProgram] = useState(null);
  const set = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    // New batch only: suggest the aligned end date from the program duration; still editable.
    if (k === "start_date" && !initial.batch_id && program?.duration_days && e.target.value) {
      next.end_date = new Date(new Date(e.target.value).getTime() + (program.duration_days - 1) * 86400000)
        .toISOString()
        .slice(0, 10);
    }
    setForm(next);
  };

  useEffect(() => {
    api.get("/programs").then(({ data }) => setProgram(data.data.find((p) => p.program_id === programId) || null)).catch(() => {});
  }, [programId]);

  // Duration is derived from the batch calendar dates; it is never a stored batch field.
  const calendarDays =
    form.start_date && form.end_date
      ? Math.floor((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1
      : 0;
  const suggestedEnd =
    form.start_date && program?.duration_days
      ? new Date(new Date(form.start_date).getTime() + (program.duration_days - 1) * 86400000).toISOString().slice(0, 10)
      : null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity), code: form.code.toUpperCase() };
      if (isEdit) {
        const { code, ...rest } = payload;
        await api.patch(`/admin/batches/${initial.batch_id}`, rest);
      } else await api.post("/admin/batches", { ...payload, program_id: programId });
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) {
      toast.error(extractError(err).message);
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid={PROG.batchForm} className="bg-[#10121B] border-[#FF3B30]/30">
      <CardContent className="p-6">
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("programs.batchName")} testId={PROG.batchName} value={form.name} onChange={set("name")} required />
          <Field label={t("programs.batchCode")} testId={PROG.batchCode} value={form.code} onChange={set("code")} placeholder="B01" disabled={isEdit} required maxLength={12} />
          <DateField label={t("programs.startDate")} testId={PROG.batchStart} value={form.start_date} onChange={(v) => set("start_date")({ target: { value: v } })} clearable={false} required />
          <DateField label={t("programs.endDate")} testId={PROG.batchEnd} value={form.end_date} onChange={(v) => set("end_date")({ target: { value: v } })} clearable={false} required />
          {calendarDays > 0 && (
            <p data-testid="batch-form-duration-hint" className="sm:col-span-2 -mt-2 text-xs text-slate-500">
              {calendarDays} {t("programs.calendarDays")}
              {program?.duration_days ? ` · ${t("programs.durationDays")} ${program.duration_days}${suggestedEnd ? ` · ${t("programs.alignedEnd")} ${suggestedEnd}` : ""}` : ""}
            </p>
          )}
          <Field label={t("programs.capacity")} testId={PROG.batchCapacity} type="number" min={1} value={form.capacity} onChange={set("capacity")} required />
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-sm">{t("common.status")}</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid={PROG.batchStatus} className="bg-[#1C202E] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                {["OPEN", "CLOSED", "RUNNING", "COMPLETED", "ARCHIVED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-white/5">{t("common.cancel")}</Button>
            <Button data-testid={PROG.batchSave} type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">{busy ? t("common.loading") : t("common.save")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
